"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Hammer, Brain, Wrench, Lightbulb, Users, Presentation, Activity } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
function useRobotoLang() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const getStoredLang = () => {
      if (typeof window === "undefined") return "vi";
      const stored = localStorage.getItem("roboto_lang");
      return stored === "en" || stored === "vi" ? stored : "vi";
    };
    setLang(getStoredLang());
    const handleLangChange = () => {
      setLang(getStoredLang());
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  return lang;
}
const items = [{
  id: 1,
  vi: "Con trực tiếp xây dựng sản phẩm",
  en: "Your child builds hands-on",
  icon: Hammer,
  num: "01"
}, {
  id: 2,
  vi: "Con phải suy nghĩ trước khi lập trình",
  en: "Your child thinks before coding",
  icon: Brain,
  num: "02"
}, {
  id: 3,
  vi: "Con thử nghiệm và sửa lỗi",
  en: "Your child tests and debugs",
  icon: Wrench,
  num: "03"
}, {
  id: 4,
  vi: "Con học cách giải quyết vấn đề",
  en: "Your child learns to solve problems",
  icon: Lightbulb,
  num: "04"
}, {
  id: 5,
  vi: "Con làm việc cùng bạn",
  en: "Your child works with teammates",
  icon: Users,
  num: "05"
}, {
  id: 6,
  vi: "Con trình bày ý tưởng / sản phẩm khi phù hợp",
  en: "Your child presents ideas and projects when appropriate",
  icon: Presentation,
  num: "06"
}];
export default function WhatParentsCanObserve() {
  const lang = useRobotoLang();
  const heading = {
    vi: "PHỤ HUYNH CÓ THỂ QUAN SÁT ĐIỀU GÌ TRONG QUÁ TRÌNH HỌC?",
    en: "What Can Parents Observe During the Learning Process?"
  };
  const badgeText = {
    vi: "HOẠT ĐỘNG LỚP HỌC THỰC TẾ",
    en: "REAL CLASSROOM ACTION"
  };
  return <section id="whatparentscanobserve" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      {/* Background shape accent */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-secondary/15 blur-2xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-accent/15 blur-2xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 lg:mb-16">
          <motion.h2 initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.35
        }} className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            {heading[lang]}
          </motion.h2>

          {/* Precision Lime Green Spec Rail Anchor */}
          <div className="mt-6 flex items-center justify-center gap-2">
            <div className="h-0.5 w-12 bg-secondary rounded-full" />
            <div className="h-1.5 w-1.5 rounded-full bg-secondary" />
            <div className="h-0.5 w-24 bg-secondary/40 rounded-full" />
          </div>
        </div>

        {/* Content Section: Supporting Photograph + Checklist Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
          {/* Supporting Photography Panel */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="lg:col-span-5 flex flex-col">
            <div className="relative rounded-2xl overflow-hidden border border-border bg-slate-900 text-white shadow-xl h-full flex flex-col justify-between group min-h-[360px] lg:min-h-[480px]">
              {/* Background Photo */}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_classroom_observation_lab.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_classroom_observation_lab.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_classroom_observation_lab.png-wvc-sizes" alt="ROBOTO academy students collaborating on robotics engineering and coding task" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105" />

              {/* Technical Gradient Scrim */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-900/40 to-slate-900/20" />

              {/* Top Technical Badge Bar */}
              <div className="relative z-10 p-5 flex items-center justify-between">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-950/80 backdrop-blur-md border border-white/20 text-xs font-semibold text-white uppercase tracking-wider">
                  <Activity className="size-3.5 text-secondary animate-pulse" />
                  {badgeText[lang]}
                </span>
                <span className="text-[11px] font-mono font-medium text-white/70 bg-slate-950/60 px-2.5 py-1 rounded border border-white/10">
                  SYS.OBS-06
                </span>
              </div>

              {/* Bottom Spec & Accent Overlay */}
              <div className="relative z-10 p-6 sm:p-8 backdrop-blur-xs bg-slate-950/40 border-t border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-1 w-8 bg-secondary rounded-full" />
                  <span className="text-xs font-mono uppercase tracking-widest text-secondary font-bold">
                    ROBOTO TECH LAB
                  </span>
                </div>
                <p className="text-sm text-slate-200 leading-relaxed font-medium">
                  {lang === "vi" ? "Môi trường học tập tiêu chuẩn R&D sinh động, kích thích tư duy chủ động và kỹ năng giải quyết vấn đề của trẻ." : "An active R&D-grade academy lab environment encouraging proactive thinking and hardware debugging."}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Observable Items Grid */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {items.map((item, index) => {
            const Icon = item.icon;
            return <motion.div key={item.id} data-index={index} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.35,
              delay: index * 0.05,
              ease: "easeOut"
            }}>
                  <Card className="h-full border border-border bg-card hover:border-secondary/50 shadow-xs hover:shadow-md transition-all duration-300 hover:-translate-y-1 group relative overflow-hidden">
                    {/* Subtle top accent highlight on hover */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-secondary to-secondary/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    <CardContent className="p-5 sm:p-6 flex flex-col h-full justify-between gap-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="size-11 rounded-xl bg-secondary/15 text-secondary flex items-center justify-center shrink-0 group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors duration-300">
                          <Icon className="size-5" />
                        </div>
                        <span className="text-xs font-mono font-semibold text-muted-foreground/60 tracking-wider">
                          {item.num}
                        </span>
                      </div>

                      <div>
                        <p className="text-base font-semibold text-foreground leading-snug">
                          {item[lang]}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}